import React from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import { Button, Image } from 'components/ui';
import localeUtils from 'utils/LanguageLocale';
import { mediaQueries } from 'style/config';

/**
 * AfterPay Pill Button - Release 4 Style
 *
 * Renders a black pill-shaped button with the Afterpay logo.
 * When clicked, triggers the AfterPayExpandedView (L3 state) instead of
 * immediately selecting Afterpay as payment method.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isAfterpayEnabledForThisOrder - Backend validation: order is eligible
 * @param {boolean} props.isAfterpayEnabledForThisProfile - User profile is eligible for Afterpay
 * @param {Function} props.handleAfterPayExpand - Callback to expand AfterPayExpandedView (L3 state)
 * @param {number|string|Array} [props.width] - Button width, defaults to ['100%', '151px', '100%'] (responsive: mobile, tablet, desktop)
 * @param {number} [props.height] - Button height, defaults to 44px
 * @param {string} [props.imageHeight] - Logo height, defaults to '24px'
 * @returns {JSX.Element|null} Black pill button or null if not eligible
 */
const AfterPayPill = props => {
    const {
        isAfterpayEnabledForThisOrder, isAfterpayEnabledForThisProfile, handleAfterPayExpand, width, height, imageHeight, imageScale
    } = props;

    // Eligibility check: Don't render if Afterpay is not enabled for this order or user profile
    if (!isAfterpayEnabledForThisOrder || !isAfterpayEnabledForThisProfile) {
        return null;
    }

    // Locale-specific logo: Canada uses different Afterpay branding
    const isCanada = localeUtils.isCanada();
    const logoSrc = isCanada ? '/img/ufe/logo-afterpay-ca-white.svg' : '/img/ufe/logo-afterpay-white.svg';
    const scale = imageScale && !isCanada ? imageScale : 1;

    const handleClick = () => {
        if (handleAfterPayExpand) {
            handleAfterPayExpand();
        }
    };

    return (
        <Button
            variant='primary'
            onClick={handleClick}
            data-at={Sephora.debug.dataAt('afterpay_button')}
            data-testid='afterpay-pill-button'
            aria-label='View Afterpay payment options'
            aria-expanded='false'
            css={styles}
            height={height || 44}
            minHeight={height || 44}
            width={width || ['100%', '151px', '100%']}
        >
            <Image
                src={logoSrc}
                alt=''
                aria-hidden='true'
                height={imageHeight || '24px'}
                css={{
                    transform: `scale(${scale})`
                }}
                minWidth='67px'
            />
        </Button>
    );
};

/**
 * Afterpay pill button styles
 */
const styles = {
    backgroundColor: '#000',
    borderRadius: '22px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px 24px',
    gap: '12px',
    flexShrink: 0,
    [mediaQueries.xsMax]: {
        padding: '8px 16px'
    },
    '&:hover': {
        backgroundColor: '#333'
    }
};

export default wrapFunctionalComponent(AfterPayPill, 'AfterPayPill');
